import baseStyles from "../..//toolbox.module.css";
import { useEffect, useState, useCallback } from "react";
import styles from "./toolbox-route-source.module.css";
import { Toolbox, BasicToolboxProps } from "../../toolbox";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { loadGPXFromFileInputEvent } from "PosterEditor/utils/file-import";
import { ToolboxDeleteButton } from "../../toolbox-components/toolbox-component-delete-button";

interface ToolboxRouteSourceProps extends BasicToolboxProps {}

export const ToolboxRouteSource = (props: ToolboxRouteSourceProps) => {
  const { project, gpsSource } = usePosterProject();

  const getFirstUploadedFile = useCallback(() => {
    const first = gpsSource?.name;
    if (first) {
      return {
        name: first,
        numberOfPoints: gpsSource?.allCoordinates.length ?? 0,
      };
    }
  }, [gpsSource?.name]);

  const firstUploadedFile = getFirstUploadedFile();

  return (
    <Toolbox
      title="GPS Source"
      key={firstUploadedFile ? "uploaded" : "import"}
      collapsible={props.collapsible}
      isCollapsed={props.isCollapsed}
      onToggleCollapse={props.onToggleCollapse}
    >
      <div key={firstUploadedFile ? "uploaded" : "import"}>
        {firstUploadedFile ? (
          <UploadedFileList file={firstUploadedFile} />
        ) : (
          <CoordinateImport />
        )}
      </div>
    </Toolbox>
  );
};

const CoordinateImport = () => {
  const { project, setCoordinates, gpsSource } = usePosterProject();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const data = await loadGPXFromFileInputEvent(event);
    let name = data?.name ?? event.target.files?.[0].name ?? "No file name";
    if (data?.coordinates) {
      setCoordinates(data.coordinates, name);
    }
  };

  const sourceFileName = gpsSource?.name ?? " ";

  return (
    <div className={styles.container}>
      <div className={styles.topContainer}>
        <span className={baseStyles.explainerText}>
          {sourceFileName}
          Upload a .GPX file from your computer
        </span>
        <FileUploadButton handleFileUpload={handleFileUpload} />
      </div>
      <div className={styles.bottomContainer}>
        <img
          className={styles.gpxIcon}
          src="/pallete-components/GPX-ICON.svg"
          alt="GPX file icon"
        />
        <div className={styles.gpxExplainerTextContainer}>
          <span className={baseStyles.explainerText}>
            GPX files contain coordinates of your runs/hikes, and can be found
            on Strava.com
          </span>
          <a
            href="https://support.strava.com/hc/en-us/articles/216918437-Exporting-your-Data-and-Bulk-Export#:~:text=not%20estimated%20power.-,Navigate%20to%20one%20of%20your%20Activity%20pages%20and%20from%20the,rate%2C%20cadence%2C%20and%20temperature."
            className={baseStyles.link}
            target="_blank"
            rel="noreferrer"
          >
            Get GPX files from Strava
          </a>
        </div>
      </div>
    </div>
  );
};

export const FileUploadButton = ({
  handleFileUpload,
}: {
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <div className={styles.uploadButtonWrapper}>
      <input
        id="file-upload"
        className={styles.uploadButton}
        type="file"
        accept=".gpx"
        onChange={handleFileUpload}
      />
      <label htmlFor="file-upload" className={styles.uploadButtonLabel}>
        Upload File
      </label>
    </div>
  );
};

interface UploadedFile {
  name: string;
  numberOfPoints: number;
}

interface UploadedFileListProps {
  file: UploadedFile;
}

const UploadedFileList = (props: UploadedFileListProps) => {
  const { deleteAllCoordinates } = usePosterProject();

  const numberOfPointsText =
    props.file.numberOfPoints === 1
      ? "1 point"
      : `${props.file.numberOfPoints} points`;

  const onClickDelete = () => {
    deleteAllCoordinates();
  };

  return (
    <div className={styles.container}>
      <div className={styles.topContainer}>
        <div className={styles.uploadedFileContainer}>
          <div className={styles.uploadFileContainerTextContainer}>
            <span className={styles.uploadedFileTitle}>{props.file.name}</span>
            <span className={styles.uploadedFileSubtitle}>
              {numberOfPointsText}
            </span>
          </div>
          <ToolboxDeleteButton onclick={onClickDelete} />
        </div>
      </div>
    </div>
  );
};
